import type { InferencePrice } from '@famecreator/shared-types/inference-price';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { inferencePriceService } from '@/shared/api/inference-price-service';

type VideoResolutionPricing = {
  perSecond: number | null; // per-second rate (same for audio and non-audio)
};

type ContentPricing = {
  image: { price: number | null };
  video: {
    '480p': VideoResolutionPricing;
    '720p': VideoResolutionPricing;
    '1080p': VideoResolutionPricing;
  };
  audio: {
    price: number | null;
    characterUnit: number | null; // For Speech Generation (ElevenLabs)
  };
  ugcTalk: {
    pricePerSecond: number | null;
    durationUnit: number | null; // For UGC Talk (OmniHuman)
  };
};
type InferencePriceStore = {
  prices: ContentPricing | null;
  rawPrices: InferencePrice[];
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  fetchPrices: () => Promise<void>;
  getPrice: (mediaType: string, resolution?: string, duration?: number) => number | null;
  getVideoPrice: (resolution: string, duration: number, audio?: boolean) => number | null; // Per-second video pricing
  calculateAudioPrice: (text: string) => number; // Calculate Speech Generation price based on text length
  calculateUgcTalkPrice: (durationSeconds: number) => number; // Calculate UGC Talk price based on duration
};

const createEmptyResolutionPricing = (): VideoResolutionPricing => ({
  perSecond: null,
});

// Helper function to create empty content pricing structure
const createEmptyContentPricing = (): ContentPricing => ({
  image: { price: null },
  video: {
    '480p': createEmptyResolutionPricing(),
    '720p': createEmptyResolutionPricing(),
    '1080p': createEmptyResolutionPricing(),
  },
  audio: { price: null, characterUnit: null },
  ugcTalk: { pricePerSecond: null, durationUnit: null },
});

// Function to structure the raw prices into organized format
const structurePrices = (rawPrices: InferencePrice[]): ContentPricing => {
  const structured: ContentPricing = createEmptyContentPricing();

  // Filter only enabled prices and process them
  rawPrices
    .filter((price) => price.enabled)
    .forEach((price) => {
      switch (price.mediaType) {
        case 'image':
          structured.image.price = price.credits;
          break;

        case 'audio':
          // Speech Generation: provider = null or "eleven" (ElevenLabs) with characterUnit
          if (!price.provider || price.provider === 'eleven') {
            structured.audio.price = price.credits;
            structured.audio.characterUnit = price.characterUnit || null;
          }
          break;

        case 'video':
          // Check if this is UGC Talk pricing (provider = "omnihuman")
          if (price.provider === 'omnihuman') {
            structured.ugcTalk.pricePerSecond = price.credits;
            structured.ugcTalk.durationUnit = price.durationUnit || null;
            break;
          }

          // Per-second video pricing: same rate regardless of audio
          if (price.videoResolution && price.durationUnit === 1) {
            const resolution = price.videoResolution as '480p' | '720p' | '1080p';
            structured.video[resolution].perSecond = price.credits;
          }
          break;
      }
    });

  return structured;
};

export const useInferencePriceStore = create<InferencePriceStore>()(
  devtools(
    (set, get) => ({
      prices: null,
      rawPrices: [],
      isLoading: false,
      error: null,
      lastUpdated: null,

      fetchPrices: async () => {
        const state = get();

        // Avoid duplicate requests
        if (state.isLoading) return;

        // Use cached data if it's less than 5 minutes old
        if (state.lastUpdated && state.prices) {
          const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
          if (state.lastUpdated > fiveMinutesAgo) {
            return;
          }
        }

        set({ isLoading: true, error: null });

        try {
          const rawPrices = await inferencePriceService.getInferencePrices();
          const structuredPrices = structurePrices(rawPrices);

          set({
            prices: structuredPrices,
            rawPrices,
            isLoading: false,
            lastUpdated: new Date(),
            error: null,
          });
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Failed to fetch prices',
          });
        }
      },

      getPrice: (mediaType: string, resolution?: string, duration?: number) => {
        const state = get();
        if (!state.prices) return null;

        switch (mediaType) {
          case 'image':
            return state.prices.image.price;

          case 'audio':
            return state.prices.audio.price;

          case 'video': {
            if (!resolution || !duration) return null;
            return state.getVideoPrice(resolution, duration);
          }

          default:
            return null;
        }
      },

      // Get video price: rate per second × duration
      // DB stores the "with audio" rate. Without audio = half the rate.
      getVideoPrice: (resolution: string, duration: number, audio: boolean = true) => {
        const state = get();
        if (!state.prices) return null;

        const resPricing = state.prices.video[resolution as '480p' | '720p' | '1080p'];
        if (!resPricing) return null;

        if (resPricing.perSecond !== null) {
          const rate = audio ? resPricing.perSecond : resPricing.perSecond / 2;
          return rate * duration;
        }

        return null;
      },

      // Calculate Speech Generation (ElevenLabs) price based on character count
      calculateAudioPrice: (text: string) => {
        const state = get();
        if (!state.prices?.audio.price || !state.prices?.audio.characterUnit) {
          return state.prices?.audio.price || 0;
        }

        const trimmedText = text.trim();
        const textLength = trimmedText.length;

        if (textLength === 0) return 0;

        // Calculate: ceil(text_length / character_unit) * credits_per_unit
        const units = Math.ceil(textLength / state.prices.audio.characterUnit);
        return units * state.prices.audio.price;
      },

      // Calculate UGC Talk (OmniHuman) price based on duration
      calculateUgcTalkPrice: (durationSeconds: number) => {
        const state = get();
        if (!state.prices?.ugcTalk.pricePerSecond || !state.prices?.ugcTalk.durationUnit) {
          return state.prices?.ugcTalk.pricePerSecond || 0;
        }

        if (durationSeconds <= 0) return 0;

        // Calculate: ceil(duration_seconds / duration_unit) * credits_per_unit
        const units = Math.ceil(durationSeconds / state.prices.ugcTalk.durationUnit);
        return units * state.prices.ugcTalk.pricePerSecond;
      },

      reset: () => {
        set({
          prices: null,
          rawPrices: [],
          isLoading: false,
          error: null,
          lastUpdated: null,
        });
      },
    }),
    {
      name: 'inference-price-store',
    },
  ),
);
